import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, SearchX } from "lucide-react";
import { api } from "@/lib/api";
import SEO from "@/lib/seo";
import { useContent } from "@/lib/content";
import Reveal from "@/components/Reveal";
import ProductCard from "@/components/ProductCard";

export default function Boutique() {
  const { t } = useContent();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState("");
  const activeCat = searchParams.get("categorie") || "";

  useEffect(() => {
    api
      .get("/categories")
      .then((r) => setCategories(r.data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (activeCat) params.category = activeCat;
    if (search.trim()) params.search = search.trim();
    const t = setTimeout(() => {
      api
        .get("/products", { params })
        .then((r) => setProducts(r.data))
        .catch(() => {})
        .finally(() => setLoading(false));
    }, 300);
    return () => clearTimeout(t);
  }, [activeCat, search]);

  const activeCatName = useMemo(
    () => categories.find((c) => c.slug === activeCat)?.name,
    [categories, activeCat]
  );

  return (
    <div data-testid="boutique-page">
      <SEO title={activeCatName ? `${activeCatName} — Boutique` : "La Boutique"} description="Matériel d'allaitement à Dakar : tire-lait Medela, recueil de lait Haakaa, coquillages d'allaitement, crème lanoline, sachets de conservation, coussinets lavables et compléments Calmosine. Livraison à domicile à Dakar, paiement à la livraison." />
      <section className="bg-cream pt-36 pb-14 md:pb-20">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <Reveal>
            <p className="eyebrow mb-3">{t("shop.eyebrow")}</p>
            <h1 className="font-serif text-4xl md:text-6xl text-teal-dark">
              {activeCatName ? activeCatName : t("shop.title")}
            </h1>
            <p className="mt-4 max-w-xl text-ink/70">
              {t("shop.sub")}
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-10 md:py-14 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="flex flex-col lg:flex-row lg:items-center gap-6 justify-between mb-10">
            <div className="flex flex-wrap gap-2" data-testid="boutique-filters">
              <button
                data-testid="boutique-filter-all"
                onClick={() => setSearchParams({})}
                className={`rounded-full px-5 py-2 text-sm border transition-colors ${!activeCat ? "bg-teal text-cream border-teal" : "border-line text-ink hover:border-teal hover:text-teal"}`}
              >
                {t("shop.all")}
              </button>
              {categories.map((c) => (
                <button
                  key={c.slug}
                  data-testid={`boutique-filter-${c.slug}`}
                  onClick={() => setSearchParams({ categorie: c.slug })}
                  className={`rounded-full px-5 py-2 text-sm border transition-colors ${activeCat === c.slug ? "bg-teal text-cream border-teal" : "border-line text-ink hover:border-teal hover:text-teal"}`}
                >
                  {c.name}
                </button>
              ))}
            </div>
            <div className="relative w-full lg:w-72">
              <Search size={17} className="absolute left-4 top-1/2 -translate-y-1/2 text-mauve" />
              <input
                data-testid="boutique-search-input"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t("shop.search")}
                className="w-full rounded-full border border-line bg-cream-light py-3 pl-11 pr-4 text-sm outline-none focus:border-teal focus:ring-2 focus:ring-mint/40 transition"
              />
            </div>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {["a", "b", "c", "d", "e", "f"].map((k) => (
                <div key={`skeleton-${k}`} className="rounded-3xl bg-cream-light border border-line h-96 animate-pulse" />
              ))}
            </div>
          ) : products.length === 0 ? (
            <div data-testid="boutique-empty-state" className="flex flex-col items-center gap-4 py-20 text-center">
              <span className="flex h-16 w-16 items-center justify-center rounded-full bg-cream">
                <SearchX size={26} className="text-teal" />
              </span>
              <p className="text-ink/70 max-w-sm">Aucun produit ne correspond à votre recherche — essayez un autre mot-clé ou parcourez toutes les catégories.</p>
              <button onClick={() => { setSearch(""); setSearchParams({}); }} className="rounded-full bg-teal px-6 py-2.5 text-sm text-cream hover:bg-teal-dark transition-colors">
                Voir tous les produits
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {products.map((p, i) => (
                <ProductCard key={p.id} product={p} index={i} />
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
